import React, { useEffect, useMemo, useState } from 'react';
import {Card,Row,Col,Typography,Button,Avatar,Space,Tag,Divider,Modal,List,Checkbox,} from 'antd';
import {CheckCircleOutlined,SafetyCertificateOutlined,FieldTimeOutlined,CloseCircleOutlined,LockOutlined,GlobalOutlined,} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../../actions/authActions';

const { Title, Paragraph } = Typography;

const AcceptInvitation: React.FC = () => {

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const verifyToken = useSelector((state: any) => state.verifyTokenReducer);
  const invitationToken = useSelector((state: any) => state.acceptInvitationReducer);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);


  const token = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get('token') || '';
  }, [location.search]);

  useEffect(() => {
    if (token) {
      dispatch(authActions.verifyTokenRequest({ token, isInvite: true }));
    }
  }, [token, dispatch]);


  useEffect(() => {
    if (invitationToken?.isSuccess) {
      const authToken = localStorage.getItem('AUTHDATA');
      if (authToken) {
        setShowSessionModal(true);
      } else {
        navigate('/login');
      }
    }
  }, [invitationToken?.isSuccess, navigate]);
  
  
  const dataSource = useMemo(() => {
    const termsContent =
      verifyToken?.verifyData?.terms?.[0]?.content || [];

    const privacyItems = [
      {
        key: 'dpdp',
        title: 'Legal compliance',
        icon: <GlobalOutlined style={{ color: '#096dd9' }} />,
      },
      {
        key: 'use',
        title: 'Usage of information',
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      },
      {
        key: 'no-sell',
        title: 'No sale of personal data',
        icon: <LockOutlined style={{ color: '#fa8c16' }} />,
      },
      {
        key: 'security',
        title: 'Security measures',
        icon: (
          <SafetyCertificateOutlined style={{ color: '#13c2c2' }} />
        ),
      },
    ];

    return privacyItems.map((item, index) => ({
      ...item,
      desc: termsContent[index] || '',
    }));
  }, [verifyToken?.verifyData]);


  const handleValidate = () => {
    dispatch(
      authActions.acceptInvitationRequest({
        invitationToken: token,
      })
    );
  };

  const handleAcceptClick = () => {
    setShowPrivacyModal(true);
  };

  const handleAgree = () => {
    setShowPrivacyModal(false);
    setAgreeChecked(false);
    handleValidate();
  };

  const handleGoToLogin = () => {
    localStorage.removeItem('AUTHDATA');
    setShowSessionModal(false);
    navigate('/login');
  };

  const handleContinueSession = () => {
    setShowSessionModal(false);
    navigate('/');
  };

  const isVerified = verifyToken?.isSuccess;

  return (
    <div className="accept-invite-root">
      <Card className="accept-invite-card">
        <Row gutter={[28, 28]} align="middle">
          {/* LEFT SIDE */}
          <Col xs={24} md={11} className="accept-invite-left">
            <div className="invite-hero-stack">
              <div className="invite-hero-circle">
                <div className="invite-hero-ring" />
                <div className="invite-hero-inner">
                  <Avatar size={64} icon={ isVerified ? ( <SafetyCertificateOutlined /> ) : ( <CloseCircleOutlined style={{ color: 'red' }} /> )
                    }
                    className="invite-hero-avatar"
                  />
                </div>
              </div>

              <Title level={3} className="invite-title">
                {isVerified ? "You're invited" : 'Invitation Expired'}
              </Title>
              <Paragraph type="secondary" className="invite-paragraph" >
                {isVerified ? 'Please accept the invitation to continue' : 'Invitation has expired'}
              </Paragraph>
            <Space>
                <Tag color="green">Secure</Tag>
                <Tag icon={<FieldTimeOutlined />}color="processing" >
                  Time-limited
                </Tag>
              </Space>
            </div>
          </Col>
          <Col xs={24} md={13} className="accept-invite-right">
            <div className="invite-right-stack">
              <Divider className="invite-divider" />
              <div className="invite-actions">
                <Button type="primary" onClick={handleAcceptClick} disabled={!isVerified} icon={<CheckCircleOutlined />}>
                  Accept
                </Button>
              </div>
            </div>
          </Col>
        </Row>
      </Card>
      <Modal
        title={ <div style={{ display: 'flex', alignItems: 'center', gap: 12,}}>
            <Avatar size={34} style={{ backgroundColor: '#e6f7ff',color: '#1890ff',}}icon={<SafetyCertificateOutlined />}/>
            <div>
              <div style={{ fontWeight: 700 }}>
                Privacy & Data Use
              </div>
              <div style={{fontSize: 12,color: '#6b7280',}}>
                How we handle your personal information
              </div>
            </div>
          </div>
        }
        open={showPrivacyModal}
        onCancel={() => { setShowPrivacyModal(false);setAgreeChecked(false);
        }}
        footer={[
          <Button key="cancel" onClick={() => { setShowPrivacyModal(false); setAgreeChecked(false);}}>
            Cancel
          </Button>,
          <Button key="agree" type="primary" onClick={handleAgree} disabled={!agreeChecked} loading={invitationToken?.isLoading}
          >
            I Agree
          </Button>,
        ]}
      >
        <Typography>
          <Paragraph style={{ marginBottom: 8 }}> Please review how we use your personal information. By agreeing you confirm that you have read and accept
            these terms.
          </Paragraph>

          <List itemLayout="horizontal" dataSource={dataSource} renderItem={(item) => (
              <List.Item>
                <List.Item.Meta avatar={ <Avatar size={40} style={{ background: 'transparent' }} > {item.icon} </Avatar> }
                  title={ <span style={{ fontWeight: 600 }}> {item.title} </span> }
                  description={ <span style={{ color: '#374151' }}> {item.desc} </span>}
                />
              </List.Item>
            )}
          />

          <div style={{ marginTop: 12 }}>
            <Checkbox checked={agreeChecked} onChange={(e) => setAgreeChecked(e.target.checked) } >
              I have read and agree to the Privacy & Data
              Use statements.{' '}
             
            </Checkbox>
          </div>
        </Typography>
      </Modal>
      <Modal
        title="Session Detected"
        open={showSessionModal}
        onCancel={() => setShowSessionModal(false)}
        closable={false}
        maskClosable={false}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Button key="login" onClick={handleGoToLogin}>
              Log in with a different account
            </Button>
            <Button key="continue" type="primary" onClick={handleContinueSession}>
              Continue to my account
            </Button>
          </div>
        }
      >
        <Typography>
          <Paragraph style={{ marginBottom: 0 }}>
            You're already signed in. Would you like to continue with your current account, or log in again?
          </Paragraph>
        </Typography>
      </Modal>
    </div>
  );
};

export default AcceptInvitation;
